
import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment';
import { getCompanyName } from '@/api/company';
import { UserModule } from '@/store/user';
import { getPosts } from '@/api/irPost';
import { getUserInfo } from '@/api/user';
import type { IIrPostSummary } from '@/types/irPost';

@Component({
  name: 'IrPostList',
})
export default class extends Vue {
/* //////////// */
private subVisualStyleEl: HTMLStyleElement | null = null;

 private originalSubVisualHTML: string | null = null;

 mounted(): void {
   this.injectSubVisualStyle();
   this.injectSubVisualContent();
 }

 beforeDestroy(): void {
   this.removeSubVisualStyle();
   this.restoreSubVisualContent();
 }

 private injectSubVisualStyle(): void {
   const style = document.createElement('style');
   style.setAttribute('data-from', 'IrPostList');

   style.textContent = `
   .sub_visual.sub_visual01 {
     display: block !important;
     height: auto !important;
     height: 160px !important;
     padding: 20px !important;
     background: none !important;
     overflow: visible !important;
     position: relative !important;
     visibility: visible !important;
     opacity: 1 !important;
   }

   .sub_visual::before,
   .sub_visual::after,
   .sub_visual01::before,
   .sub_visual01::after {
     content: none !important;
     display: none !important;
   }

   #sub_lnb,
   #sub_lnb * {
     color: #000 !important;
     opacity: 1 !important;
     visibility: visible !important;

     -webkit-text-fill-color: #000 !important;
     text-shadow: none !important;
     mix-blend-mode: normal !important;
     filter: none !important;
   }

   #sub_lnb {
     display: block !important;
     position: relative !important;
     z-index: 10 !important;
     background: #fff !important;
     padding: 10px 0;
   }

   .sub_lnb_navi {text-align:right;}
   `;

   document.head.appendChild(style);
   this.subVisualStyleEl = style;
 }

 private removeSubVisualStyle(): void {
   if (this.subVisualStyleEl) {
     document.head.removeChild(this.subVisualStyleEl);
     this.subVisualStyleEl = null;
   }
 }

 private injectSubVisualContent(): void {
   this.$nextTick(() => {
     const subVisual = document.querySelector('.sub_visual');

     if (!subVisual) {
       setTimeout(() => this.injectSubVisualContent(), 50);
       return;
     }

     this.originalSubVisualHTML = subVisual.innerHTML;

     subVisual.innerHTML = `
       <div id="sub_lnb">
         <div class="sub_lnb_box">
           <div class="sub_lnb_ttl">IR매칭</div>
           <div class="sub_lnb_navi">
             <ul>
               <li>HOME</li>
               <li>마이페이지</li>
               <li>IR매칭</li>
             </ul>
           </div>
         </div>
       </div>
     `;
   });
 }

 private restoreSubVisualContent(): void {
   const subVisual = document.querySelector('.sub_visual');

   if (subVisual && this.originalSubVisualHTML !== null) {
     subVisual.innerHTML = this.originalSubVisualHTML;
     this.originalSubVisualHTML = null;
   }
 }
 /*  ////////// */

  private postList: IIrPostSummary[] = [];

  private currentPage = 1;

  private pageSize = 10;

  private total = 0;

  get totalPages(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  private getStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      A: '승인 대기',
      B: '진행중',
      C: '진행중',
      D: '관리자 반려',
      E: '투자사 거절',
      F: 'IR매칭 취소',
      S: 'IR매칭 완료',
    };
    return statusMap[status] || status;
  }

  private getStatusClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      A: 'warning',
      B: 'primary',
      C: 'primary',
      D: 'danger',
      E: 'danger',
      F: 'danger',
      S: 'success',
    };
    return statusMap[status] || 'info';
  }

  private formatDate(date: string): string {
    return moment(date).format('YYYY-MM-DD HH:mm');
  }

  private handleRowClick(row: IIrPostSummary): void {
    if (row.status === 'A') {
      return; // Prevent navigation for '승인 대기' status
    }
    this.$router.push({
      name: 'ChatDetail',
      params: {
        menuUid: this.$route.params.menuUid,
        postUid: String(row.postId),
      },
    });
  }

  private prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage -= 1;
      this.fetchPostList();
    }
  }

  private nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage += 1;
      this.fetchPostList();
    }
  }

  private handleSizeChange(): void {
    this.currentPage = 1;
    this.fetchPostList();
  }

  private async fetchPostList(): Promise<void> {
    if (!UserModule.token) {
      alert('로그인이 필요합니다.');
      this.$router.push('/login');
      return;
    }

    try {
      const uRes = await getUserInfo();
      const { uid, roles } = uRes.data;
      // console.log(uRes.data);

      if (!uid) {
        console.error('fetchPostList: 사용자 정보에서 UID를 가져올 수 없습니다.');
        return;
      }

      UserModule.setUserUid(uid);

      const isAdmin = roles && roles.includes('ROLE_ADMIN');

      const params: any = {
        page: this.currentPage - 1,
        size: this.pageSize,
        sort: 'modifyDate,desc',
      };

      if (!isAdmin) {
        params.fromUserUid = uid;
      }

      const response = await getPosts(params);
      const posts = response.data.content;

      // ✅ 각 게시글의 상대방 회사명 조회
      this.postList = await Promise.all(
        posts.map(async (post: IIrPostSummary) => {
          // 현재 사용자가 보낸 글이면 toUserUid로, 받은 글이면 fromUserUid로 회사명을 조회
          const targetUserUid = post.fromUserUid === uid ? post.toUserUid : post.fromUserUid;

          try {
            const res = await getCompanyName(targetUserUid);
            return {
              ...post,
              companyName: res.data.coName, // 상대방 회사명 추가
            };
          } catch (error) {
            console.error(`회사 정보 조회 실패 (${targetUserUid}):`, error);
            return {
              ...post,
              companyName: targetUserUid, // 실패 시 UID 그대로 표시
            };
          }
        }),
      );

      this.total = response.data.totalElements;

      // console.log('게시글 목록:', this.postList);
      // console.log('총 개수:', this.total);
    } catch (error) {
      console.error('게시글 목록을 불러오는데 실패했습니다.', error);
    }
  }

  private async created(): Promise<void> {
    await this.fetchPostList();
  }
}

